import type { ReactNode } from 'react';

import type { IAccountFormProps } from '../accountFormProps';
import type { AccountFormV2Props } from './AccountFormV2';

/** Legacy suppresses a heading with `''` (`{headerText && …}`); V2 uses `null`. */
function legacyHeading(text: string | undefined): ReactNode {
  return text === '' ? null : text;
}

/**
 * Bridges the legacy container's ~35-prop `IAccountFormProps` down to the fields
 * the V2 shell reads. The legacy contract has no `onSuccess`/`onError` equivalent
 * — its `onLogin`/`onRegister` hand the write to the consumer, which V2 owns — so
 * none is forwarded and the shell's own completion finishes the flow.
 * `onRegistrationSuccess` crosses as the observability channel for that owned
 * write: it is how a consumer hears about a registration it no longer performs.
 *
 * The headings have to cross over: cosmos computes real user-facing copy into
 * them, including the `?updatedPassword=true` confirmation that is the only thing
 * telling a user why a password change bounced them back to a login form.
 * The sub-header props stay dropped; nothing passes them to a V2-reachable mount.
 *
 * `email` crosses too, as the entry field's seed. Cosmos fills it from
 * `?emailPrefill=`, which a Rails-generated participant-invitation email
 * produces (`leads/invite_participant.rb`) and cake forwards onto `/login`
 * (`platform/loginRedirect.ts`) — so without it an invitee retypes the address
 * their own invite carried. It only prefills: legacy also auto-submits the
 * lookup and skips the entry screen, which V2 does not reproduce (see
 * `PARITY.md`).
 *
 * `onPasswordlessLoginRequest` crosses because it is the only path to a real
 * sign-in link: the shell's fallback send mails a password email, not a login
 * link, and the consumer's handler is also what carries `redirectTo` into the
 * passwordless mutation.
 */
export function adaptLegacyAccountFormProps(
  props: IAccountFormProps,
): Pick<
  AccountFormV2Props,
  | 'redirectTo'
  | 'heading'
  | 'registrationHeading'
  | 'initialEmail'
  | 'onPasswordlessLoginRequest'
  | 'onRegistrationSuccess'
> {
  return {
    redirectTo: props.loginRedirectTo,
    heading: legacyHeading(props.loginHeaderText),
    registrationHeading: legacyHeading(props.registrationHeaderText),
    initialEmail: props.email,
    onPasswordlessLoginRequest: props.onPasswordlessLoginRequest,
    onRegistrationSuccess: props.onRegistrationSuccess,
  };
}
